import { formatMoney } from './g-utils'

class CartProgressBar extends HTMLElement {
  static observedAttributes = ['data-free-delivery-amount', 'data-free-product-amount', 'data-amount-left']

  constructor() {
    super()
    this.freeDeliveryAmountValueEls = this.querySelectorAll('[data-free-delivery-amount-value]')
    this.freeProductAmountValueEls = this.querySelectorAll('[data-free-product-amount-value]')
    this.amountLeftValueEls = this.querySelectorAll('[data-amount-left-value]')
  }

  connectedCallback() {
    document.documentElement.addEventListener('cart:rerender', this._rerender.bind(this))
  }

  attributeChangedCallback(name, oldValue, newValue) {
    switch (name) {
      case 'data-free-delivery-amount':
        this.freeDeliveryAmountValueEls.forEach(
          (item) => (item.textContent = formatMoney(newValue).replace(',00', '').replace('.00', ''))
        )
        break
      case 'data-free-product-amount':
        this.freeProductAmountValueEls.forEach(
          (item) => (item.textContent = formatMoney(newValue).replace(',00', '').replace('.00', ''))
        )
        break
      case 'data-amount-left':
        this.amountLeftValueEls.forEach((item) => (item.textContent = formatMoney(newValue)))
        break
    }
  }

  async _rerender() {
    const response = await fetch('/cart.js')
    const cart = await response.json()

    // Séparer les livres neufs et d'occasion
    const newBooks = cart.items.filter((item) => item.sku.toUpperCase().includes('NW'))
    const usedBooks = cart.items.filter((item) => !item.sku.toUpperCase().includes('NW'))

    // Calculer les montants séparément
    const newBooksAmount = newBooks.reduce((acc, item) => acc + item.final_price * item.quantity, 0)
    const usedBooksAmount = usedBooks.reduce((acc, item) => acc + item.final_price * item.quantity, 0)

    // Déterminer le seuil de livraison offerte selon la présence de livres neufs
    const freeDeliveryAmount = newBooks.length > 0 
      ? Number.parseInt(this.dataset.freeDeliveryNewBooksAmount) 
      : Number.parseInt(this.dataset.freeDeliveryUsedBooksAmount)

    // Récupérer le seuil de produit offert
    const freeProductAmount = Number.parseInt(this.dataset.freeProductAmount)

    // Calculer les montants restants (≥ 0)
    const deliveryAmountLeft = Math.max(
      freeDeliveryAmount - (newBooks.length > 0 ? newBooksAmount : usedBooksAmount),
      0
    )
    const productAmountLeft = Math.max(freeProductAmount - usedBooksAmount, 0)

    // Déterminer l'étape actuelle
    let currentStep = 'complete'
    let currentAmountLeft = 0
    let currentThreshold = freeDeliveryAmount

    if (deliveryAmountLeft > 0) {
      currentStep = 'delivery'
      currentAmountLeft = deliveryAmountLeft
      currentThreshold = freeDeliveryAmount
    } else if (productAmountLeft > 0) {
      currentStep = 'product'
      currentAmountLeft = productAmountLeft
      currentThreshold = freeProductAmount
    }

    // Mettre à jour les classes CSS - retirer toutes les classes d'étape
    this.classList.remove('js-cart-current-step-delivery', 'js-cart-current-step-product')
    this.classList.remove('js-reached-delivery', 'js-reached-product', 'js-reached')

    // Ajouter les classes appropriées
    if (newBooks.length > 0) {
      this.classList.add('js-new-books')
    } else {
      this.classList.remove('js-new-books')
    }

    if (deliveryAmountLeft === 0) {
      this.classList.add('js-reached-delivery')
    }

    if (productAmountLeft === 0) {
      this.classList.add('js-reached-product')
    }

    if (currentStep === 'delivery') {
      this.classList.add('js-cart-current-step-delivery')
    } else if (currentStep === 'product') {
      this.classList.add('js-cart-current-step-product')
    } else if (currentStep === 'complete') {
      this.classList.add('js-reached')
    }

    // Mettre à jour les montants via les setters (qui déclenchent attributeChangedCallback)
    this.freeDeliveryAmount = freeDeliveryAmount
    this.freeProductAmount = freeProductAmount
    this.amountLeft = currentAmountLeft

    // Calculer la progression de la barre
    let progress = 100
    if (currentStep !== 'complete') {
      progress = ((currentThreshold - currentAmountLeft) / currentThreshold) * 100
    }
    this.style.setProperty('--progress', progress + '%')
  }

  set freeDeliveryAmount(value) {
    this.dataset.freeDeliveryAmount = value
  }

  get freeDeliveryAmount() {
    return Number.parseInt(this.dataset.freeDeliveryAmount)
  }

  set freeProductAmount(value) {
    this.dataset.freeProductAmount = value
  }

  get freeProductAmount() {
    return Number.parseInt(this.dataset.freeProductAmount)
  }

  set amountLeft(value) {
    this.dataset.amountLeft = Math.max(value, 0)
  }

  get amountLeft() {
    return Number.parseInt(this.dataset.amountLeft)
  }
}

if (!customElements.get('cart-progress-bar')) {
  customElements.define('cart-progress-bar', CartProgressBar)
}
